import __request__ from 'routing/request';
interface PaginationInfo {
    readonly per_page: number;
    readonly total: number;
    readonly count: number;
    readonly page: number;
}
interface BulkOperationFailure {
    readonly failure_explanation: string;
    readonly entity_id: string;
}
interface Config {
    readonly type: string;
}
interface FilterOption {
    readonly title: string;
    readonly value: string;
}
interface ValidationResult {
    readonly error_context: {
        readonly [_key: string]: string[];
    };
    readonly failed: boolean;
    readonly errors: {
        readonly [_key: string]: string[];
    };
}
interface SchedulerCtx {
    readonly next_time: string;
    readonly data: JobTriggerData;
    readonly triggered_at: string;
    readonly queued_notifications: number;
    readonly is_scheduled: boolean;
    readonly status: 'runnable' | 'running' | 'complete' | 'paused' | 'error' | 'cancelled';
}
interface EventFieldSpec {
    readonly data_type: 'string' | 'error';
    readonly providers: Config[];
}
interface EntityShareRequest {
    readonly selected_grantee_capabilities: {
        readonly [_key: string]: 'view' | 'manage' | 'own';
    };
}
interface EventDefinitionDto {
    readonly field_spec: {
        readonly [_key: string]: EventFieldSpec;
    };
    readonly event_procedure: string;
    readonly key_spec: string[];
    readonly notification_settings: EventNotificationSettings;
    readonly description: string;
    readonly _scope: string;
    readonly storage: Config[];
    readonly remediation_steps: string;
    readonly priority: number;
    readonly title: string;
    readonly scheduler: SchedulerCtx;
    readonly updated_at: string;
    readonly matched_at: string;
    readonly alert: boolean;
    readonly id: string;
    readonly state: 'ENABLED' | 'DISABLED';
    readonly config: EventProcessorConfig;
    readonly notifications: Config[];
}
interface JobTriggerData {
    readonly type: string;
}
type EventDefinitionDtoArray = EventDefinitionDto[];
interface PageListResponse_EventDefinitionDto {
    readonly total: number;
    readonly pagination: PaginationInfo;
    readonly defaults: EntityDefaults;
    readonly query: string;
    readonly elements: EventDefinitionDto[];
    readonly attributes: EntityAttribute[];
    readonly sort: string;
    readonly order: 'asc' | 'desc';
}
interface BulkOperationResponse {
    readonly failures: BulkOperationFailure[];
    readonly successfully_performed: number;
    readonly errors: string[];
}
interface EntityAttribute {
    readonly filterable: boolean;
    readonly related_property: string;
    readonly related_collection: string;
    readonly hidden: boolean;
    readonly sortable: boolean;
    readonly id: string;
    readonly type: 'STRING' | 'DATE' | 'DOUBLE' | 'INT' | 'LONG' | 'OBJECT_ID' | 'BOOLEAN';
    readonly title: string;
    readonly searchable: boolean;
    readonly filter_options: FilterOption[];
}
interface CronValidationResponse {
    readonly description: string;
    readonly error: string;
}
interface GetByIdRequest {
    readonly event_definition_ids: string[];
}
interface Sorting {
    readonly id: string;
    readonly direction: 'ASC' | 'DESC';
}
interface EventNotificationSettings {
    readonly backlog_size: number;
    readonly grace_period_ms: number;
}
interface EventDefinitionWithContext {
    readonly context: {
        readonly [_key: string]: Object;
    };
    readonly is_mutable: boolean;
    readonly event_definition: EventDefinitionDto;
}
interface CronValidationRequest {
    readonly expression: string;
}
interface EntityDefaults {
    readonly sort: Sorting;
}
interface EventProcessorParameters {
    readonly type: string;
}
interface UnwrappedCreateEntityRequest_EventDefinitionDto {
    readonly field_spec: {
        readonly [_key: string]: EventFieldSpec;
    };
    readonly event_procedure: string;
    readonly key_spec: string[];
    readonly notification_settings: EventNotificationSettings;
    readonly description: string;
    readonly _scope: string;
    readonly storage: Config[];
    readonly remediation_steps: string;
    readonly priority: number;
    readonly title: string;
    readonly share_request: EntityShareRequest;
    readonly scheduler: SchedulerCtx;
    readonly updated_at: string;
    readonly matched_at: string;
    readonly alert: boolean;
    readonly id: string;
    readonly state: 'ENABLED' | 'DISABLED';
    readonly config: EventProcessorConfig;
    readonly notifications: Config[];
}
interface BulkOperationRequest {
    readonly entity_ids: string[];
}
interface EventProcessorConfig {
    readonly type: string;
}
interface PaginatedResponse_EventDefinitionDto {
    readonly [_key: string]: Object;
}
/**
 * List event definitions
 */
export function list(page: number = 1, per_page: number = 50, query?: string): Promise<PaginatedResponse_EventDefinitionDto> {
    return __request__('GET', '/events/definitions', null, { 'page': page, 'per_page': per_page, 'query': query }, {
        'Accept': ['application/json']
    });
}
/**
 * Create new event definition
 * @param schedule schedule
 */
export function create(schedule: boolean = true, JSONBody?: UnwrappedCreateEntityRequest_EventDefinitionDto): Promise<EventDefinitionDto> {
    return __request__('POST', '/events/definitions', JSONBody, { 'schedule': schedule }, {
        'Accept': ['application/json']
    });
}
/**
 * Delete multiple event definitions
 */
export function bulkDelete(Entitiestoremove: BulkOperationRequest): Promise<BulkOperationResponse> {
    return __request__('POST', '/events/definitions/bulk_delete', Entitiestoremove, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Enable multiple event definitions
 */
export function bulkSchedule(Eventdefinitionstoenable: BulkOperationRequest): Promise<BulkOperationResponse> {
    return __request__('POST', '/events/definitions/bulk_schedule', Eventdefinitionstoenable, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Disable multiple event definitions
 */
export function bulkUnschedule(Eventdefinitionstodisable: BulkOperationRequest): Promise<BulkOperationResponse> {
    return __request__('POST', '/events/definitions/bulk_unschedule', Eventdefinitionstodisable, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get multiple event definitions by id
 */
export function getById(JSONbody?: GetByIdRequest): Promise<EventDefinitionDtoArray> {
    return __request__('POST', '/events/definitions/byIds', JSONbody, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get a paginated list of event definitions
 * @param sort The field to sort the result on
 * @param order The sort direction
 */
export function getPage(sort: 'title' | 'description' | 'priority' | 'status' = 'title', page: number = 1, per_page: number = 50, query?: string, order: 'asc' | 'desc' = 'asc'): Promise<PageListResponse_EventDefinitionDto> {
    return __request__('GET', '/events/definitions/paginated', null, { 'page': page, 'per_page': per_page, 'query': query, 'sort': sort, 'order': order }, {
        'Accept': ['application/json']
    });
}
/**
 * Validate an event definition
 */
export function validate(JSONbody: EventDefinitionDto): Promise<ValidationResult> {
    return __request__('POST', '/events/definitions/validate', JSONbody, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Validate a cron expression
 */
export function validateByJSONbody(JSONbody: CronValidationRequest): Promise<CronValidationResponse> {
    return __request__('POST', '/events/definitions/validate/cron_expression', JSONbody, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get an event definition
 */
export function get(definitionId?: string): Promise<EventDefinitionDto> {
    return __request__('GET', `/events/definitions/${definitionId}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Update existing event definition
 * @param schedule schedule
 */
export function update(definitionId?: string, schedule: boolean = true, JSONBody?: EventDefinitionDto): Promise<EventDefinitionDto> {
    return __request__('PUT', `/events/definitions/${definitionId}`, JSONBody, { 'schedule': schedule }, {
        'Accept': ['application/json']
    });
}
/**
 * Delete event definition
 */
export function remove(definitionId?: string): Promise<EventDefinitionDto> {
    return __request__('DELETE', `/events/definitions/${definitionId}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Clear queued notifications for event
 */
export function clearNotificationQueue(definitionId?: string): Promise<void> {
    return __request__('PUT', `/events/definitions/${definitionId}/clear-notification-queue`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Duplicate an event definition
 */
export function duplicate(definitionId?: string): Promise<EventDefinitionDto> {
    return __request__('POST', `/events/definitions/${definitionId}/duplicate`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Execute event definition
 */
export function execute(parameters: EventProcessorParameters, definitionId?: string): Promise<void> {
    return __request__('POST', `/events/definitions/${definitionId}/execute`, parameters, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Enable event definition
 */
export function schedule(definitionId?: string): Promise<EventDefinitionDto> {
    return __request__('PUT', `/events/definitions/${definitionId}/schedule`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Disable event definition
 */
export function unschedule(definitionId?: string): Promise<EventDefinitionDto> {
    return __request__('PUT', `/events/definitions/${definitionId}/unschedule`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get an event definition
 */
export function getWithContext(definitionId?: string): Promise<EventDefinitionWithContext> {
    return __request__('GET', `/events/definitions/${definitionId}/with-context`, null, {}, {
        'Accept': ['application/json']
    });
}
